import React from 'react';
import { Box, Button, Grid, IconButton, Paper, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslate } from 'react-admin';
import { FilterState } from '../types';
import { FilterField } from './FilterField';
import DepartmentFilterField from '../../prestataires/DepartmentFilterField';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { THRESHOLD_LEVEL1_NAME, THRESHOLD_LEVEL2_NAME, THRESHOLD_LEVEL3_NAME } from '../../../utils/rikutec';

interface FilterPanelProps {
    filters: FilterState;
    onFilterChange: (key: keyof FilterState, value: string | boolean | number[]) => void;
    onClear: () => void;
    onClose: () => void;
}

const useStyles = makeStyles({
    paper: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        bottom: '16px',
        zIndex: 1001,
        padding: '24px',
        minWidth: '400px',
        maxWidth: '500px',
        maxHeight: 'calc(100% - 32px)',
        overflowY: 'auto',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        '@media (max-width:600px)': {
            // Anclado a los bordes del contenedor del mapa (no al viewport):
            // left+right+width:auto garantiza que quepa dentro del contenedor,
            // sin desbordar por la derecha como ocurría con calc(100vw - 32px).
            top: '8px',
            left: '8px',
            right: '8px',
            bottom: '8px',
            width: 'auto',
            minWidth: 0,
            maxWidth: 'none',
            padding: '16px',
        },
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        borderBottom: '1px solid #e3e8ee',
        paddingBottom: '16px',
    },
    title: {
        margin: 0,
        fontWeight: 700,
        fontSize: '20px',
        color: '#092b4b',
    },
    closeButton: {
        padding: '4px',
    },
    sectionTitle: {
        fontWeight: 600,
        color: '#092b4b',
        marginBottom: '16px',
        fontSize: '16px',
        borderBottom: '1px solid #e3e8ee',
        paddingBottom: '8px',
    },
    sectionTitleWithMargin: {
        fontWeight: 600,
        color: '#092b4b',
        marginBottom: '16px',
        marginTop: '8px',
        fontSize: '16px',
        borderBottom: '1px solid #e3e8ee',
        paddingBottom: '8px',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #e0e0e0',
    },
    actionButton: {
        textTransform: 'none',
        padding: '8px 24px',
        fontSize: '14px',
    },
});

export const FilterPanel: React.FC<FilterPanelProps> = React.memo(
    ({ filters, onFilterChange, onClear, onClose }) => {
        const classes = useStyles();
        const t = useTranslate();
        const isMobile = useIsMobile();

        // En móvil los campos se apilan a ancho completo (las columnas de
        // escritorio quedan demasiado apretadas con etiquetas largas).
        const half = isMobile ? 12 : 6;
        const third = isMobile ? 12 : 4;

        const thresholdOptions = [
            { value: '', label: 'Tous' },
            { value: THRESHOLD_LEVEL1_NAME, label: t('app.ancs.level1') },
            { value: THRESHOLD_LEVEL2_NAME, label: t('app.ancs.level2') },
            { value: THRESHOLD_LEVEL3_NAME, label: t('app.ancs.level3') },
        ];

        return (
            <Paper elevation={8} className={classes.paper}>
                {/* Header */}
                <Box className={classes.header}>
                    <Typography variant="h6" className={classes.title}>
                        {t('app.ancs.actions.personalize')}
                    </Typography>
                    <IconButton size="small" onClick={onClose} className={classes.closeButton}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={isMobile ? 2 : 3}>
                    {/* Department Filter */}
                    <Grid item xs={12}>
                        <DepartmentFilterField
                            value={filters.departmentCodes}
                            onChange={(codes) => onFilterChange('departmentCodes', codes)}
                        />
                    </Grid>

                    {/* Serial Number Filter */}
                    <Grid item xs={12}>
                        <FilterField
                            label={t('app.ancs.serialNumber')}
                            value={filters.productSerialNumber}
                            onChange={(value) => onFilterChange('productSerialNumber', value)}
                            type="text"
                            placeholder="Rechercher par numéro de série..."
                        />
                    </Grid>

                    {/* Product Type Filter */}
                    <Grid item xs={12}>
                        <FilterField
                            label={t('app.ancs.metadataAvatarType.name')}
                            value={filters.metadataAvatarTypeId}
                            onChange={(value) => onFilterChange('metadataAvatarTypeId', value)}
                            type="text"
                            placeholder="Rechercher par type de produit..."
                        />
                    </Grid>

                    {/* Product Typology Filter */}
                    <Grid item xs={12}>
                        <FilterField
                            label={t('app.ancs.productTypology')}
                            value={filters.groupMetadataAvatarTypeId}
                            onChange={(value) => onFilterChange('groupMetadataAvatarTypeId', value)}
                            type="text"
                            placeholder="Rechercher par typologie..."
                        />
                    </Grid>

                    {/* Date Range Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" className={classes.sectionTitle}>
                            Périodes
                        </Typography>
                    </Grid>

                    {/* Installation Date Range */}
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.after')} - ${t('app.ancs.installingDate')}`}
                            value={filters.productInstallationDateAfter || ''}
                            onChange={(value) => onFilterChange('productInstallationDateAfter', value)}
                            type="date"
                        />
                    </Grid>
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.before')} - ${t('app.ancs.installingDate')}`}
                            value={filters.productInstallationDateBefore || ''}
                            onChange={(value) => onFilterChange('productInstallationDateBefore', value)}
                            type="date"
                        />
                    </Grid>

                    {/* Maintenance Date Range */}
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.after')} - ${t('app.ancs.maintenanceLastDate')}`}
                            value={filters.productLastMaintenanceDateAfter || ''}
                            onChange={(value) => onFilterChange('productLastMaintenanceDateAfter', value)}
                            type="date"
                        />
                    </Grid>
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.before')} - ${t('app.ancs.maintenanceLastDate')}`}
                            value={filters.productLastMaintenanceDateBefore || ''}
                            onChange={(value) => onFilterChange('productLastMaintenanceDateBefore', value)}
                            type="date"
                        />
                    </Grid>

                    {/* Sludge Emptying Date Range */}
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.after')} - ${t('app.ancs.sludgeEmptyingLastDate')}`}
                            value={filters.productLastEmptyingDateAfter || ''}
                            onChange={(value) => onFilterChange('productLastEmptyingDateAfter', value)}
                            type="date"
                        />
                    </Grid>
                    <Grid item xs={half}>
                        <FilterField
                            label={`${t('app.common.before')} - ${t('app.ancs.sludgeEmptyingLastDate')}`}
                            value={filters.productLastEmptyingDateBefore || ''}
                            onChange={(value) => onFilterChange('productLastEmptyingDateBefore', value)}
                            type="date"
                        />
                    </Grid>

                    {/* Threshold Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" className={classes.sectionTitleWithMargin}>
                            Seuils
                        </Typography>
                    </Grid>

                    {/* Threshold Filters */}
                    <Grid item xs={half}>
                        <FilterField
                            label={t('app.traceabilityNotesThreshold.maintenance.name')}
                            value={filters.productLastMaintenanceThresholdName}
                            onChange={(value) => onFilterChange('productLastMaintenanceThresholdName', value)}
                            type="select"
                            options={thresholdOptions}
                        />
                    </Grid>
                    <Grid item xs={half}>
                        <FilterField
                            label={t('app.traceabilityNotesThreshold.emptying.name')}
                            value={filters.productLastEmptyingThresholdName}
                            onChange={(value) => onFilterChange('productLastEmptyingThresholdName', value)}
                            type="select"
                            options={thresholdOptions}
                        />
                    </Grid>

                    {/* Maintenance Planning Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" className={classes.sectionTitleWithMargin}>
                            Prévision de maintenance
                        </Typography>
                    </Grid>

                    {/* Maintenance Filters */}
                    <Grid item xs={third}>
                        <FilterField
                            label="Maintenance dans 3 mois"
                            value={filters.maintenanceIn3Months}
                            onChange={(value) => onFilterChange('maintenanceIn3Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={third}>
                        <FilterField
                            label="Maintenance dans 6 mois"
                            value={filters.maintenanceIn6Months}
                            onChange={(value) => onFilterChange('maintenanceIn6Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={third}>
                        <FilterField
                            label="Maintenance dans 12 mois"
                            value={filters.maintenanceIn12Months}
                            onChange={(value) => onFilterChange('maintenanceIn12Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FilterField
                            label="Hors maintenance"
                            value={filters.maintenanceHors}
                            onChange={(value) => onFilterChange('maintenanceHors', value)}
                            type="checkbox"
                        />
                    </Grid>

                    {/* Vidange Planning Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" className={classes.sectionTitleWithMargin}>
                            Prévision de vidange
                        </Typography>
                    </Grid>

                    {/* Vidange Filters */}
                    <Grid item xs={third}>
                        <FilterField
                            label="Vidange dans 3 mois"
                            value={filters.vidangeIn3Months}
                            onChange={(value) => onFilterChange('vidangeIn3Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={third}>
                        <FilterField
                            label="Vidange dans 6 mois"
                            value={filters.vidangeIn6Months}
                            onChange={(value) => onFilterChange('vidangeIn6Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={third}>
                        <FilterField
                            label="Vidange dans 12 mois"
                            value={filters.vidangeIn12Months}
                            onChange={(value) => onFilterChange('vidangeIn12Months', value)}
                            type="checkbox"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FilterField
                            label="Hors vidange"
                            value={filters.vidangeHors}
                            onChange={(value) => onFilterChange('vidangeHors', value)}
                            type="checkbox"
                        />
                    </Grid>
                </Grid>

                {/* Filter Actions */}
                <Box className={classes.actions}>
                    <Button
                        variant="outlined"
                        onClick={onClear}
                        size="medium"
                        className={classes.actionButton}
                    >
                        Effacer tous les filtres
                    </Button>
                </Box>
            </Paper>
        );
    },
);

FilterPanel.displayName = 'FilterPanel';
